import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useAllowedEmployeeIds from '../../hooks/useAllowedEmployeeIds';
import useEmployeeReportText from './employeeReportText';
import { isoDay, toDate } from './employeeReportColumns';

const lower = (value) => String(value ?? '').toLocaleLowerCase('tr-TR');

/**
 * Data of Çalışan Raporları: centers and projects, the project / center
 * choice, one row per worker per project assignment (Projects.employees),
 * the SMO Lead / region manager visibility rule (useAllowedEmployeeIds, as
 * Operasyon Yönetimi), the filters, the visits check and the Excel export.
 */
const useEmployeeReports = () => {
  const { employeeId, userAuthorization } = useUserProfile();
  const { label } = useEmployeeReportText();
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  // SMO Lead / Bölge Yöneticisi görünürlük kısıtı (null = kısıtlama yok, Set = izinli id'ler)
  const allowedEmployeeIds = useAllowedEmployeeIds(isAdmin, employeeId);

  const [centers, setCenters] = useState([]);
  const [projects, setProjects] = useState([]);
  const [centersLoading, setCentersLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedRowId, setSelectedRowId] = useState(null);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Centers'));
        const centersData = querySnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
        setCenters(centersData.sort((a, b) => String(a.Adı || '').localeCompare(String(b.Adı || ''), 'tr')));
      } catch (error) {
        console.error('Error fetching centers:', error);
        toast.error(label('toast.centersError'));
      } finally {
        setCentersLoading(false);
      }
    };
    const fetchProjects = async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const projectsData = projectsSnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
        setProjects(projectsData.sort((a, b) => (a.titleShort || '').toString().localeCompare((b.titleShort || '').toString(), 'tr')));
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error(label('toast.projectsError'));
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchCenters();
    fetchProjects();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Non-admin users may only pick projects / centers their employees work on
  const visibleProjects = useMemo(() => {
    if (isAdmin) return projects;
    if (!allowedEmployeeIds) return [];
    return projects.filter((project) => (project.employees || []).some((emp) => emp.id && allowedEmployeeIds.has(emp.id)));
  }, [projects, isAdmin, allowedEmployeeIds]);

  const visibleCenters = useMemo(() => {
    if (isAdmin) return centers;
    if (!allowedEmployeeIds) return [];
    const allowedCenterIds = new Set();
    projects.forEach((project) => {
      (project.employees || []).forEach((emp) => {
        if (emp.id && emp.centerId && allowedEmployeeIds.has(emp.id)) allowedCenterIds.add(emp.centerId);
      });
    });
    return centers.filter((center) => allowedCenterIds.has(center.id));
  }, [centers, projects, isAdmin, allowedEmployeeIds]);

  /**
   * One row per worker per project assignment (the old page built the same
   * rows after a project / center was chosen; it now lists everything first).
   */
  const rows = useMemo(() => {
    const centerNames = new Map(centers.map((center) => [center.id, center.Adı]));
    const list = [];
    projects.forEach((project) => {
      if (selectedProject && project.id !== selectedProject) return;
      (project.employees || []).forEach((emp, index) => {
        if (selectedCenter && emp.centerId !== selectedCenter) return;
        if (!isAdmin && (!allowedEmployeeIds || !allowedEmployeeIds.has(emp.id))) return;
        const assigned = toDate(emp.assignedAt);
        list.push({
          id: `${emp.id}-${project.id}-${index}`,
          workerId: emp.id,
          name: emp.name || '',
          surname: emp.surname || '',
          fullName: `${emp.name || ''} ${emp.surname || ''}`.trim(),
          projectId: project.id,
          projectTitle: project.titleShort || '',
          centerId: emp.centerId || '',
          centerName: centerNames.get(emp.centerId) || '',
          role: emp.role || '',
          rate: emp.rate || null,
          rateText: emp.rate?.type ? `${emp.rate.type} - ${emp.rate.value || ''}` : '',
          assignedAt: emp.assignedAt,
          assignedDay: isoDay(assigned),
        });
      });
    });
    return list.sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr') || a.projectTitle.localeCompare(b.projectTitle, 'tr'));
  }, [projects, centers, selectedProject, selectedCenter, isAdmin, allowedEmployeeIds]);

  const filteredRows = useMemo(() => {
    const term = lower(searchTerm.trim());
    return rows.filter((row) => {
      if (term && !lower(row.fullName).includes(term)) return false;
      if (filters.name && !lower(row.fullName).includes(lower(filters.name))) return false;
      if (filters.center && row.centerName !== filters.center) return false;
      if (filters.project && row.projectTitle !== filters.project) return false;
      if (filters.role && row.role !== filters.role) return false;
      if (filters.rate && !lower(row.rateText).includes(lower(filters.rate))) return false;
      if (filters.assignedAt && row.assignedDay !== filters.assignedAt) return false;
      return true;
    });
  }, [rows, searchTerm, filters]);

  const options = useMemo(() => {
    const unique = (values) => Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, 'tr'));
    return {
      center: unique(rows.map((row) => row.centerName)),
      project: unique(rows.map((row) => row.projectTitle)),
      role: unique(rows.map((row) => row.role)),
    };
  }, [rows]);

  const stats = useMemo(() => ({
    centers: visibleCenters.length,
    projects: visibleProjects.length,
    workers: new Set(filteredRows.map((row) => row.workerId)).size,
    assignments: filteredRows.length,
  }), [visibleCenters, visibleProjects, filteredRows]);

  const selectedRow = useMemo(() => rows.find((row) => row.id === selectedRowId) || null, [rows, selectedRowId]);
  const workerRows = useCallback((workerId) => rows.filter((row) => row.workerId === workerId), [rows]);

  const handleFilter = useCallback((key, value) => setFilters((current) => ({ ...current, [key]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    setSelectedCenter('');
    setSelectedProject('');
  }, []);

  /** True when the worker has visits on the project (else a message, as before). */
  const hasVisits = async (row) => {
    try {
      const visitsSnapshot = await getDocs(query(collection(db, 'Visits'), where('employeeId', '==', row.workerId), where('projectId', '==', row.projectId)));
      if (visitsSnapshot.empty) {
        toast.info(label('toast.noVisits'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error(label('toast.visitsError'));
      return false;
    }
  };

  const downloadExcel = () => {
    const dataToExport = filteredRows.map((row) => {
      const assigned = toDate(row.assignedAt);
      return {
        'Çalışan Adı': row.fullName,
        'Merkez': row.centerName || '-',
        'Proje': row.projectTitle,
        'Rol': row.role,
        'Tarife Tipi': isAdmin ? (row.rate?.type || '') : '',
        'Tarife Değeri': isAdmin ? (row.rate?.value || '') : '',
        'Atanma Tarihi': assigned ? assigned.toLocaleDateString('tr-TR') : '',
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');

    // File name from the chosen project / center
    const selectedProjectName = projects.find((project) => project.id === selectedProject)?.titleShort || 'Tüm_Projeler';
    const selectedCenterName = centers.find((center) => center.id === selectedCenter)?.Adı || 'Tüm_Merkezler';
    let filename = 'Çalışan_Listesi';
    if (selectedProject) filename = `${selectedProjectName}_${filename}`;
    if (selectedCenter) filename = `${filename}_${selectedCenterName}`;
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    toast.success(label('toast.excelDone'));
  };

  return {
    isAdmin,
    isLoading: centersLoading || projectsLoading,
    centers,
    projects,
    visibleCenters,
    visibleProjects,
    selectedProject,
    setSelectedProject,
    selectedCenter,
    setSelectedCenter,
    searchTerm,
    setSearchTerm,
    filters,
    handleFilter,
    clearFilters,
    rows,
    filteredRows,
    options,
    stats,
    selectedRow,
    setSelectedRowId,
    workerRows,
    hasVisits,
    downloadExcel,
  };
};

export default useEmployeeReports;
